import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { seekSupabase } from '@/integrations/supabase/seekClient';
import { useSeekAuth } from '@/contexts/SeekAuthContext';
import { useToast } from '@/hooks/use-toast';

export interface CandidateNotification {
  id: string;
  candidate_id: string;
  notification_type: string;
  title: string;
  body: string | null;
  payload_json: Record<string, unknown> | null;
  delivery_channel: string | null;
  read_at: string | null;
  created_at: string;
}

export function useSeekNotifications() {
  const { user } = useSeekAuth();
  const { toast } = useToast();
  const qc = useQueryClient();
  const uid = user?.id;

  const query = useQuery({
    queryKey: ['seek-notifications', uid],
    queryFn: async () => {
      const { data, error } = await seekSupabase
        .from('candidate_notifications')
        .select('*')
        .eq('candidate_id', uid!)
        .order('created_at', { ascending: false })
        .limit(50);
      if (error) throw error;
      return (data ?? []) as CandidateNotification[];
    },
    enabled: !!uid,
  });

  const markRead = useMutation({
    mutationFn: async (notificationId: string) => {
      const { error } = await seekSupabase
        .from('candidate_notifications')
        .update({ read_at: new Date().toISOString() })
        .eq('id', notificationId)
        .eq('candidate_id', uid!);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['seek-notifications', uid] });
    },
  });

  const markAllRead = useMutation({
    mutationFn: async () => {
      const { error } = await seekSupabase
        .from('candidate_notifications')
        .update({ read_at: new Date().toISOString() })
        .eq('candidate_id', uid!)
        .is('read_at', null);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['seek-notifications', uid] });
      toast({ title: 'All notifications marked as read' });
    },
  });

  const unreadCount = (query.data ?? []).filter(n => !n.read_at).length;

  return {
    notifications: query.data ?? [],
    isLoading: query.isLoading,
    unreadCount,
    markRead: markRead.mutate,
    markAllRead: markAllRead.mutate,
  };
}
